export enum StorageType {
  LOCAL = 'localStorage',
  SESSION = 'sessionStorage',
}

const NOOP_STORAGE: Storage = {
  length: 0,
  clear: () => {},
  getItem: () => null,
  key: () => null,
  removeItem: () => {},
  setItem: () => {},
};

const IN_MEMORY_STORAGE: Storage = (() => {
  const items = new Map<string, string>();

  return {
    length: items.size,
    clear() {
      items.clear();
    },
    getItem(key) {
      return items.get(key) ?? null;
    },
    key(index) {
      return Array.from(items.keys())[index] ?? null;
    },
    removeItem(key) {
      items.delete(key);
    },
    setItem(key, value) {
      items.set(key, value);
    },
  };
})();

export function isStorageSupported(type: StorageType) {
  const storage: Storage | undefined = window[type];

  if (!storage) {
    return false;
  }

  try {
    const key = 'support-check';
    storage.setItem(key, '');
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function getStorage(type: StorageType): Storage {
  if (isStorageSupported(type)) {
    return window[type];
  }

  console.warn(`${type} is not supported, using in-memory implementation`);

  return IN_MEMORY_STORAGE;
}

/**
 * A storage implementation that stores in memory.
 */
export const NoopStorage: Storage = NOOP_STORAGE;

/**
 * A storage implementation that stores in memory.
 */
export const InMemoryStorage: Storage = IN_MEMORY_STORAGE;

/**
 * A LocalStorage wrapper, which stores in memory if LocalStorage is unsupported.
 */
export const LocalStorage: Storage = getStorage(StorageType.LOCAL);

/**
 * A SessionStorage wrapper, which stores in memory if SessionStorage is unsupported.
 */
export const SessionStorage: Storage = getStorage(StorageType.SESSION);
